import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { exists } from '@treasury/utils';
import '@vaadin/upload';
// eslint-disable-next-line no-duplicate-imports
import type {
    UploadErrorEvent,
    UploadFileRejectEvent,
    UploadFilesChangedEvent,
    Upload as VaadinUpload,
} from '@vaadin/upload';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { attachIcon } from '../assets/icons';
import { NotificationService } from '../services';
import { TmInputFieldBase } from './tm-input-field.base';

export const tagName = 'tm-upload';

/**
 * @fires UploadFilesChangedEvent files-change
 */
@customElement(tagName)
export class TmUpload extends TmInputFieldBase<never> {
    @property({ type: String })
    public accept?: string;

    @property({ type: Number })
    public minFiles?: number;

    @property({ type: Number })
    public maxFileSize?: number;

    @InjectProperty()
    declare private readonly notificationService: NotificationService;

    private error?: Error;

    private readonly uploadRef = createRef<VaadinUpload>();

    protected validate() {
        return !exists(this.error);
    }

    protected render() {
        return html`
            <vaadin-upload
                ${ref(this.uploadRef)}
                @files-changed=${(e: UploadFilesChangedEvent) => {
                    const { detail } = e;
                    const filesChangedEvent = new CustomEvent('files-changed', {
                        detail,
                    });
                    this.dispatchEvent(filesChangedEvent);
                }}
                @file-reject=${(e: UploadFileRejectEvent) => {
                    const { error, file } = e.detail;
                    this.error = new Error(error);
                    this.notificationService.renderError({
                        content: `The file type "${file.type}" is not supported: ${error}`,
                        position: 'top-stretch',
                    });
                }}
                @upload-error=${(e: UploadErrorEvent) => {
                    const { file } = e.detail;
                    const message = `An error occurred while uploading file ${file.name}. Please try again.`;

                    this.error = new Error(message);
                    this.notificationService.renderError({
                        content: message,
                        position: 'top-stretch',
                    });
                }}
                @upload-success=${() => {
                    this.error = undefined;
                }}
                class="w-full"
                no-auto
                accept=${this.accept}
                minFiles=${this.minFiles}
                maxFileSize=${this.maxFileSize}
                .draggable=${false}
            >
                <tm-button slot="add-button" importance="tertiary" class="bg-transparent">
                    <div class="flex items-center text-header gap-2 text-sm">
                        <tm-icon .icon=${attachIcon} color="var(--tm-color-gray-medium)"></tm-icon>
                        <span>Attachment...</span>
                    </div>
                </tm-button>
            </vaadin-upload>
        `;
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                :host(:not([noOuterSpacing]))::part(component) {
                    padding: 0;
                    margin: 0;
                }
                *::part(start-button),
                *::part(status),
                vaadin-progress-bar {
                    display: none;
                }

                *::part(label) {
                    position: inherit;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmUpload;
    }
}
